import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  isAxiosError,
  Method,
} from 'axios';
import debugFn from 'debug';
import qs from 'qs';
import { appendQueryParam } from '../helpers';

const debug = debugFn('api-client');
debug.enabled = false;

interface Args {
  apiRoot: string;
}

/** Resolves a bearer token on demand (e.g. Auth0 getAccessTokenSilently). */
export type TokenProvider = () => Promise<string | null>;

type RequestConfig = AxiosRequestConfig & {
  noAuth?: boolean;
};

type InternalRequestConfig = InternalAxiosRequestConfig & {
  noAuth?: boolean;
};

/**
 * HTTP client that pulls access tokens per request via an optional token provider,
 * keeping Authorization headers fresh without a static push-once token.
 */
export class BaseAPIClient {
  apiRoot: string = null!;
  /** Last resolved token; used by sync helpers like generateUrl(secure). May be stale. */
  lastResolvedAuthToken: string | null = null;
  private tokenProvider: TokenProvider | null = null;
  authAs: string | null = null;
  unauthenticationCb: null | ((err: unknown) => void) = null;

  axios: AxiosInstance;

  constructor({ apiRoot }: Args) {
    this.apiRoot = apiRoot;

    this.axios = axios.create({
      baseURL: this.apiRoot,
      paramsSerializer: (params) => {
        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
        });
      },
    });

    // Pull a fresh token before every authenticated request (Auth0 caches / refreshes as needed).
    this.axios.interceptors.request.use(async (config: InternalRequestConfig) => {
      if (config.noAuth) {
        delete config.noAuth;
        return config;
      }

      if (config.headers?.Authorization) {
        return config;
      }

      try {
        const token = await this.resolveToken();
        if (token) {
          config.headers = config.headers ?? {};
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Refresh/session failed before the request was sent — force re-auth.
        this.unauthenticationCb?.(error);
        throw error;
      }

      return config;
    });
  }

  onUnauthentication(cb: (err: unknown) => void) {
    this.unauthenticationCb = cb;
  }

  /**
   * Registers a pull-based token source invoked before each authenticated request.
   * Passing null also clears the sync token cache.
   */
  setTokenProvider(provider: TokenProvider | null) {
    this.tokenProvider = provider;
    if (!provider) {
      this.lastResolvedAuthToken = null;
    }
  }

  setAuthAs(authAs: string | null) {
    this.authAs = authAs;
  }

  /** Sync cached token (may be stale). Prefer resolveToken() when a fresh token is required. */
  getMaybeStaleToken() {
    return this.lastResolvedAuthToken;
  }

  /**
   * Returns a usable access token via the provider (Auth0 cache/refresh), then last
   * cached value. Updates the sync cache when the provider succeeds.
   */
  async resolveToken(): Promise<string | null> {
    if (this.tokenProvider) {
      const token = await this.tokenProvider();
      this.lastResolvedAuthToken = token;
      return token;
    }

    return this.lastResolvedAuthToken;
  }

  request<T>(method: Method, url: string, options: RequestConfig = {}): Promise<AxiosResponse<T>> {
    const { headers: _headers, noAuth, ...restOptions } = options;

    const headers = _headers || {};

    if (this.authAs && !url.startsWith('/admin')) {
      headers['x-auth-as'] = this.authAs;
    }

    debug(`${method.toUpperCase()} ${url}`);

    return this.axios
      .request<T>({
        method,
        url,
        headers,
        noAuth,
        ...restOptions,
      } as RequestConfig)
      .catch((error: unknown) => {
        if (
          isAxiosError(error) &&
          error.response &&
          [401].includes(error.response.status) &&
          this.unauthenticationCb
        ) {
          this.unauthenticationCb(error);
        }

        if (isAxiosError(error) && error.response?.data) {
          const errorToThrow = {
            ...(error.response.data as object),
            axiosError: error,
          };

          throw errorToThrow;
        }

        throw error;
      });
  }

  async get<T = unknown>(url: string, options: AxiosRequestConfig = {}) {
    const { data } = await this.request<T>('get', url, options);

    return data;
  }

  async post<T = unknown>(url: string, data?: unknown, options: AxiosRequestConfig = {}) {
    const { data: responseData } = await this.request<T>('post', url, {
      ...options,
      data,
    });

    return responseData;
  }

  async patch<T = unknown>(url: string, data?: unknown, options: AxiosRequestConfig = {}) {
    const { data: responseData } = await this.request<T>('patch', url, {
      ...options,
      data,
    });

    return responseData;
  }

  async postFile<T = unknown>(url: string, file: File, options: AxiosRequestConfig = {}) {
    const data = new FormData();
    data.append('file', file);

    return this.post<T>(url, data, options);
  }

  async put<T = unknown>(url: string, data?: unknown, options: AxiosRequestConfig = {}) {
    const { data: responseData } = await this.request<T>('put', url, {
      ...options,
      data,
    });

    return responseData;
  }

  async delete<T = unknown>(url: string, data?: unknown, options: AxiosRequestConfig = {}) {
    const { data: responseData } = await this.request<T>('delete', url, {
      ...options,
      data,
    });

    return responseData;
  }

  generateUrl(
    path: string,
    opts: { prependBaseURL?: boolean; secure?: boolean; params?: object } = {},
  ) {
    let url =
      opts.prependBaseURL !== false && !(path.startsWith('https://') || path.startsWith('http://'))
        ? `${this.apiRoot}${path}`
        : path;

    if (opts.secure) {
      url = appendQueryParam(url, 'access_token', this.getMaybeStaleToken());

      if (this.authAs) {
        url = appendQueryParam(url, 'auth_as', this.authAs);
      }
    }

    if (opts.params) {
      Object.entries(opts.params).forEach(([key, value]) => {
        url = appendQueryParam(url, key, value);
      });
    }

    return url;
  }

  getApiRoot() {
    return this.apiRoot;
  }
}
